import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Languages, Play, Pause, RefreshCw } from 'lucide-react';
import { getAppLang, setAppLang } from './DomTranslator';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` });

/**
 * Admin panel: per-language marketing translation jobs (start / pause /
 * resume, resumable, already-translated strings are skipped) with live
 * progress, plus the user's own app display language.
 */
export const TranslationSettings = ({ isAdmin }) => {
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState('');
  const appLang = getAppLang();

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await fetch(`${API}/api/translations/jobs`, { headers: authHeaders() });
      if (r.ok) setJobs((await r.json()).jobs || []);
    } catch { /* noop */ }
  }, [isAdmin]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const act = async (lang, action) => {
    setBusy(lang);
    try {
      await fetch(`${API}/api/translations/jobs/${lang}/${action}`, { method: 'POST', headers: authHeaders() });
      await load();
    } finally { setBusy(''); }
  };

  return (
    <Card data-testid="translation-settings">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Languages className="h-4 w-4" /> Language & Translations
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <div className="text-sm font-medium mb-1.5">App display language</div>
          <div className="flex gap-2">
            {[['en', 'English'], ['hu', 'Magyar'], ['pl', 'Polski'], ['de', 'Deutsch']].map(([l, label]) => (
              <Button key={l} size="sm" variant={appLang === l ? 'default' : 'outline'}
                data-testid={`app-lang-${l}`} onClick={() => setAppLang(l)}>
                {label}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Translations are cached, each phrase is translated once by AI, then served from the database forever.
          </p>
        </div>

        {isAdmin && (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">Marketing site translation jobs (admin)</div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  disabled={busy === 'all' || jobs.every((j) => j.corpus_total && j.cached >= j.corpus_total)}
                  onClick={async () => {
                    setBusy('all');
                    try {
                      await fetch(`${API}/api/translations/jobs/resume-all`, { method: 'POST', headers: authHeaders() });
                      await load();
                    } finally { setBusy(''); }
                  }}
                  data-testid="resume-all-jobs"
                >
                  <Play className="h-3.5 w-3.5 mr-1" /> Resume all
                </Button>
                <Button size="sm" variant="ghost" onClick={load} data-testid="refresh-jobs"><RefreshCw className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            <div className="space-y-3">
              {jobs.map((j) => {
                const pct = j.corpus_total ? Math.min(100, Math.round((j.cached / j.corpus_total) * 100)) : 0;
                const running = j.status === 'running' && j.worker_alive;
                return (
                  <div key={j.lang} className="rounded-md border p-3" data-testid={`job-${j.lang}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-sm font-semibold">{j.language} <span className="text-xs text-muted-foreground uppercase ml-1">{j.lang}</span></div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${running ? 'text-green-600' : j.status === 'paused' ? 'text-amber-600' : 'text-muted-foreground'}`} data-testid={`job-status-${j.lang}`}>
                          {running ? 'running' : j.status}
                        </span>
                        {running ? (
                          <Button size="sm" variant="outline" disabled={busy === j.lang} onClick={() => act(j.lang, 'pause')} data-testid={`pause-${j.lang}`}>
                            <Pause className="h-3.5 w-3.5 mr-1" /> Pause
                          </Button>
                        ) : (
                          <Button size="sm" disabled={busy === j.lang || pct >= 100} onClick={() => act(j.lang, 'start')} data-testid={`start-${j.lang}`}>
                            <Play className="h-3.5 w-3.5 mr-1" /> {pct > 0 && pct < 100 ? 'Resume' : pct >= 100 ? 'Done' : 'Start'}
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="h-2 w-full rounded bg-muted overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1" data-testid={`job-progress-${j.lang}`}>
                      {j.cached} / {j.corpus_total} strings translated ({pct}%){j.failed ? ` · ${j.failed} failed (retried on next run)` : ''}
                    </div>
                  </div>
                );
              })}
              {!jobs.length && <div className="text-xs text-muted-foreground">Loading job status…</div>}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Start once per language. Pause anytime, resuming skips everything already translated. Refresh shows live progress.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TranslationSettings;
